import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useFormValidation } from '../../hooks/useFormValidation';
import DistribuidorSeoJsonLd from './components/SeoJsonLd';

export default function Distribuidor() {
  const [formData, setFormData] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Form validation
  const { errors, touched, validateEmail, validatePhone, validateName, sanitizeInput, handleBlur, handleChange, validateAll, resetValidation } = useFormValidation({
    nombre: {
      required: true,
      minLength: 2,
      custom: (value) => {
        if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]{2,}$/.test(value)) {
          return 'Solo se permiten letras y espacios';
        }
        return null;
      }
    },
    empresa: {
      required: true,
      minLength: 2,
    },
    email: {
      required: true,
      custom: (value) => {
        if (!validateEmail(value)) {
          return 'Formato de email inválido';
        }
        return null;
      }
    },
    telefono: {
      minLength: 7,
      custom: (value) => {
        if (value && !validatePhone(value)) {
          return 'Formato de teléfono inválido';
        }
        return null;
      }
    },
    mensaje: {
      maxLength: 500,
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    handleChange(name, value);
    if (submitStatus !== 'idle') setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar todos los campos
    const isValid = validateAll(formData);
    
    if (!isValid) {
      return;
    }

    // Prevenir doble envío
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Sanitizar inputs
      const sanitizedData = {
        nombre: sanitizeInput(formData.nombre.trim()),
        empresa: sanitizeInput(formData.empresa.trim()),
        email: sanitizeInput(formData.email.trim()),
        telefono: formData.telefono ? sanitizeInput(formData.telefono.trim()) : '',
        mensaje: formData.mensaje ? sanitizeInput(formData.mensaje.trim()) : '',
      };

      const { error } = await supabase
        .from('distributor_applications')
        .insert([
          {
            full_name: sanitizedData.nombre,
            company_name: sanitizedData.empresa,
            email: sanitizedData.email,
            phone: sanitizedData.telefono || null,
            message: sanitizedData.mensaje || null,
            status: 'pending',
          },
        ]);

      if (error) {
        console.error('Error al guardar solicitud:', error);
        setSubmitStatus('error');
      } else {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          empresa: '',
          email: '',
          telefono: '',
          mensaje: '',
        });
        resetValidation();
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const beneficios = [
    {
      icon: 'ri-percent-line',
      title: 'Descuentos Exclusivos',
      description: 'Accede a precios mayoristas con descuentos de hasta el 40% en todas las licencias.',
    },
    {
      icon: 'ri-vip-crown-line',
      title: 'Programa VIP',
      description: 'Beneficios adicionales según tu volumen de ventas mensuales.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Soporte Prioritario',
      description: 'Atención personalizada 24/7 con un ejecutivo de cuenta dedicado.',
    },
    {
      icon: 'ri-truck-line',
      title: 'Entrega Inmediata',
      description: 'Recibe las licencias al instante para entregar a tus clientes sin demoras.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Garantía Extendida',
      description: 'Todas las licencias incluyen garantía de por vida y soporte técnico.',
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Panel de Control',
      description: 'Gestiona tus pedidos, facturas y clientes desde un panel exclusivo.',
    },
  ];

  const pasos = [
    {
      numero: '01',
      titulo: 'Regístrate',
      descripcion: 'Completa el formulario con los datos de tu empresa.',
    },
    {
      numero: '02',
      titulo: 'Verificación',
      descripcion: 'Nuestro equipo verificará tu información en 24-48 horas.',
    },
    {
      numero: '03',
      titulo: 'Activación',
      descripcion: 'Recibe tus credenciales y accede a precios exclusivos.',
    },
    {
      numero: '04',
      titulo: '¡Empieza a Vender!',
      descripcion: 'Comienza a generar ingresos con nuestras licencias.',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <DistribuidorSeoJsonLd />
      <Header onSearch={() => {}} />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-24 bg-gradient-to-r from-purple-600 to-indigo-600 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/22af0178520f57e9e721cf1ae6a2085b.jpg"
            alt="Distribuidor"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative container mx-auto px-4 text-center">
          <span className="inline-block bg-white/20 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-4 sm:mb-6">
            <i className="ri-star-line mr-1 sm:mr-2"></i>
            Programa de Distribuidores
          </span>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-6">
            Conviértete en Distribuidor
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-5 sm:mb-8 px-2">
            Únete a nuestra red de distribuidores y accede a precios exclusivos, soporte prioritario y las mejores condiciones del mercado para hacer crecer tu negocio.
          </p>
          <a
            href="#formulario"
            className="inline-flex items-center gap-2 bg-white text-purple-600 px-5 py-2.5 sm:px-8 sm:py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:scale-105 cursor-pointer whitespace-nowrap text-sm sm:text-base"
          >
            <i className="ri-rocket-line"></i>
            Solicitar Acceso
          </a>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">
              Beneficios Exclusivos para Distribuidores
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-xs sm:text-base">
              Disfruta de ventajas únicas diseñadas para impulsar tu negocio y maximizar tus ganancias.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-8">
            {beneficios.map((beneficio, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-xl p-4 sm:p-8 hover:shadow-lg transition-all hover:-translate-y-1 group"
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-3 sm:mb-6 group-hover:bg-purple-600 transition-colors">
                  <i
                    className={`${beneficio.icon} text-lg sm:text-2xl text-purple-600 group-hover:text-white transition-colors`}
                  ></i>
                </div>
                <h3 className="text-sm sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-3">
                  {beneficio.title}
                </h3>
                <p className="text-gray-600 text-[11px] sm:text-base leading-relaxed">
                  {beneficio.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">
              ¿Cómo Funciona?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-xs sm:text-base">
              Convertirte en distribuidor es muy sencillo. Sigue estos pasos y comienza a vender en menos de 48 horas.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-8">
            {pasos.map((paso, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-xl p-4 sm:p-8 text-center hover:shadow-lg transition-shadow">
                  <span className="text-3xl sm:text-5xl font-bold text-purple-100">{paso.numero}</span>
                  <h3 className="text-sm sm:text-xl font-bold text-gray-900 mt-2 sm:mt-4 mb-1.5 sm:mb-3">
                    {paso.titulo}
                  </h3>
                  <p className="text-gray-600 text-[11px] sm:text-base">{paso.descripcion}</p>
                </div>
                {index < pasos.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <i className="ri-arrow-right-line text-2xl text-purple-300"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Estadísticas */}
      <section className="py-10 sm:py-16 bg-gradient-to-r from-purple-600 to-indigo-600">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center text-white">
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">500+</div>
              <div className="text-white/80 text-[11px] sm:text-base">Distribuidores Activos</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">40%</div>
              <div className="text-white/80 text-[11px] sm:text-base">Descuento Máximo</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">24/7</div>
              <div className="text-white/80 text-[11px] sm:text-base">Soporte Disponible</div>
            </div>
            <div>
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">10K+</div>
              <div className="text-white/80 text-[11px] sm:text-base">Licencias Vendidas</div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Contacto */}
      <section id="formulario" className="py-10 sm:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6 sm:mb-12">
              <h2 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">
                Solicita tu Acceso como Distribuidor
              </h2>
              <p className="text-gray-600 text-xs sm:text-base">
                Completa el formulario y nuestro equipo se pondrá en contacto contigo en menos de 24
                horas.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-5 sm:p-8 md:p-12">
              {submitStatus === 'success' ? (
                <div className="text-center py-8 sm:py-12">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <i className="ri-check-line text-3xl sm:text-4xl text-green-600"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                    ¡Solicitud Enviada!
                  </h3>
                  <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                    Gracias por tu interés. Nuestro equipo revisará tu solicitud y te contactará en
                    menos de 24 horas.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-purple-600 font-medium hover:underline cursor-pointer text-sm sm:text-base"
                  >
                    Enviar otra solicitud
                  </button>
                </div>
              ) : (
                <form id="distribuidor-form" data-readdy-form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('nombre')}
                        required
                        className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                          touched.nombre && errors.nombre
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-purple-500'
                        }`}
                        placeholder="Tu nombre"
                      />
                      {touched.nombre && errors.nombre && (
                        <p className="text-red-500 text-xs mt-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.nombre}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Nombre de la Empresa *
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('empresa')}
                        required
                        className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                          touched.empresa && errors.empresa
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-purple-500'
                        }`}
                        placeholder="Tu empresa"
                      />
                      {touched.empresa && errors.empresa && (
                        <p className="text-red-500 text-xs mt-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.empresa}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Correo Electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('email')}
                        required
                        className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                          touched.email && errors.email
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-purple-500'
                        }`}
                        placeholder="correo@empresa.com"
                      />
                      {touched.email && errors.email && (
                        <p className="text-red-500 text-xs mt-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.email}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('telefono')}
                        className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm ${
                          touched.telefono && errors.telefono
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-purple-500'
                        }`}
                        placeholder="+57 300 000 0000"
                      />
                      {touched.telefono && errors.telefono && (
                        <p className="text-red-500 text-xs mt-1">
                          <i className="ri-error-warning-line mr-1"></i>
                          {errors.telefono}
                        </p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                        Mensaje (Opcional)
                      </label>
                      <textarea
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('mensaje')}
                        rows={4}
                        maxLength={500}
                        className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border focus:ring-2 focus:border-transparent outline-none transition-all text-sm resize-none ${
                          touched.mensaje && errors.mensaje
                            ? 'border-red-300 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-purple-500'
                        }`}
                        placeholder="Cuéntanos sobre tu negocio..."
                      />
                      <div className="flex justify-between items-center mt-1">
                        {touched.mensaje && errors.mensaje ? (
                          <p className="text-red-500 text-xs">
                            <i className="ri-error-warning-line mr-1"></i>
                            {errors.mensaje}
                          </p>
                        ) : (
                          <span></span>
                        )}
                        <p className="text-xs text-gray-500 text-right">
                          {formData.mensaje.length}/500
                        </p>
                      </div>
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 sm:gap-3">
                      <i className="ri-error-warning-line text-red-500 text-lg sm:text-xl"></i>
                      <p className="text-red-700 text-xs sm:text-sm">
                        Hubo un error al enviar tu solicitud. Por favor, intenta nuevamente.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || Object.keys(errors).some(key => errors[key])}
                    className="w-full mt-6 sm:mt-8 bg-purple-600 text-white py-3 sm:py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
